import { useMemo } from 'react';
import { useElementSize } from '@mantine/hooks';
import type { IconProps } from '@tabler/icons-react';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import type { ConsolePanelTab } from '~/atoms/atoms';

export type TabItem = {
  value: ConsolePanelTab;
  label: string;
  icon?: ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;
};

export function useTabOverflow({
  tabs,
  padding = 16,
  overflowButtonWidth = 26,
  minVisibleTabs = 1,
  characterWidth = 14,
}: {
  tabs: TabItem[];
  padding?: number;
  overflowButtonWidth?: number;
  minVisibleTabs?: number;
  characterWidth?: number;
}) {
  const { ref, width } = useElementSize();

  const tabWidths = useMemo(() => {
    return tabs.map((tab) => padding + tab.label.length * characterWidth);
  }, []);

  const visibleTabCount = useMemo(() => {
    if (!width) {
      return tabs.length;
    }

    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < tabWidths.length; i++) {
      const needsOverflowButton = i < tabWidths.length - 1;
      const requiredWidth = totalWidth + tabWidths[i] + (needsOverflowButton ? overflowButtonWidth : 0);

      if (requiredWidth <= width) {
        totalWidth += tabWidths[i];
        count++;
      } else {
        // Stop as soon as we can't fit more tabs
        break;
      }
    }

    return Math.max(minVisibleTabs, count);
  }, [width]);

  const { visibleTabs, overflowTabs } = useMemo(
    () => ({
      visibleTabs: tabs.slice(0, visibleTabCount),
      overflowTabs: tabs.slice(visibleTabCount),
    }),
    [visibleTabCount],
  );

  return {
    ref,
    visibleTabs,
    overflowTabs,
    width,
  };
}
